/**
 * 视觉（图生文）API：调用画布 LLM 的 vision 模式反推提示词。
 *
 * 图片引用规则：
 *  - blob:/data: URL → 客户端转 data URL 后上传（IndexedDB 本地图，服务端无法解析）
 *  - 服务器媒体 URL / canonical key → 原样传给后端，由后端解析（带归属校验）
 */
import { platformFetch, readSSEStream } from '../../../platform/http';

async function blobToDataUrl(url: string): Promise<string> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('图片读取失败');
  const blob = await resp.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('图片编码失败'));
    reader.readAsDataURL(blob);
  });
}

/** 把节点里的图片引用转换为可提交给 vision 接口的形式 */
export async function toVisionFetchableRef(url: string): Promise<string> {
  if (url.startsWith('blob:')) return blobToDataUrl(url);
  return url;
}

export interface ReversePromptParams {
  images: string[];
  instruction?: string;
  onDelta: (delta: string) => void;
  signal?: AbortSignal;
}

/**
 * 反推提示词（流式）。服务端按 SSE 下发 { content } 增量与 [DONE]。
 */
export async function reversePromptStream(params: ReversePromptParams): Promise<void> {
  const response = await platformFetch('/canvas/llm/stream', {
    method: 'POST',
    body: JSON.stringify({
      mode: 'vision',
      images: params.images,
      instruction: params.instruction,
    }),
    signal: params.signal,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || '反推提示词失败');
  }

  let hasError: string | null = null;
  await readSSEStream(response, (payload) => {
    if (payload === '[DONE]') return;
    try {
      const obj = JSON.parse(payload) as { content?: string; error?: string };
      if (typeof obj.error === 'string' && obj.error) {
        hasError = obj.error;
        return;
      }
      if (typeof obj.content === 'string' && obj.content) {
        params.onDelta(obj.content);
      }
    } catch {
      // 非 JSON 行跳过
    }
  });
  if (hasError) throw new Error(hasError);
}

/**
 * 流结束但没收到任何内容也没收到错误（网络截断 / 后端中途崩溃）的兜底。
 * 由调用方在 reversePromptStream 返回后检查 accumulated 是否为空。
 */
export function checkVisionStreamEmpty(accumulated: string): void {
  if (!accumulated) {
    throw new Error('反推失败：未收到任何内容，请稍后重试');
  }
}
